import { Button } from "components/DesignSystem/Button/Button";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import {
  useDeleteFromRecycleBinMutation,
  useRestoreFromRecycleBinMutation,
} from "store/apis/documents";
import { fileAndFolder } from "types/Models/documents";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal";
import { RestoreConfirmation } from "./RestoreConfirmation";
import { pluralize } from "utils/pluralize";

type RecycleBinBulkActionsProps = {
  selectedItems: fileAndFolder[];
  groupId: string;
  onClearSelection: () => void;
};

export const RecycleBinBulkActions = ({
  selectedItems,
  groupId,
  onClearSelection,
}: RecycleBinBulkActionsProps) => {
  const { alertToast, successToast } = useToast();
  const deleteModal = useModal();
  const restoreModal = useModal();

  const [restoreFromRecycleBin, { isLoading: isRestoring }] =
    useRestoreFromRecycleBinMutation();
  const [deleteFromRecycleBin, { isLoading: isDeleting }] =
    useDeleteFromRecycleBinMutation();

  const selectedFileIds = selectedItems.map((item) => item.uuid);
  const selectedItemCount = selectedItems.length;

  const handleBulkRestore = async () => {
    try {
      await restoreFromRecycleBin({
        groupId,
        files: selectedFileIds,
      }).unwrap();
      successToast({
        title: "Restored successfully",
        message: `The ${pluralize(
          selectedFileIds.length,
          "file",
          "files",
          false
        )} has been moved back to its original location.`,
      });
      onClearSelection();
    } catch (error: any) {
      alertToast({
        message: error?.data?.error?.message || "Failed to restore items",
      });
    }
  };

  const handleBulkDelete = async () => {
    try {
      await deleteFromRecycleBin({
        groupId,
        files: selectedFileIds,
      }).unwrap();

      successToast({
        title: "Deleted successfully",
        message: `The ${pluralize(
          selectedFileIds.length,
          "file",
          "files",
          false
        )} has been permanently deleted`,
      });
      deleteModal.close();
      onClearSelection();
    } catch (error: any) {
      alertToast({
        message: error?.data?.error?.message || "Failed to delete items",
      });
    }
  };

  return (
    <>
      <SelectedFloatingControl>
        {selectedItemCount > 0 && (
          <>
            <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
              {selectedItemCount} selected
            </span>
            <span className="t-text-neutral-10 t-h-full">
              <DividerLine />
            </span>
            <Button
              size="small"
              onClick={restoreModal.open}
              disabled={isRestoring}
            >
              Restore
            </Button>
            <Button
              size="small"
              customType="danger_outlined"
              onClick={deleteModal.open}
            >
              Delete permanently
            </Button>
            <Button
              customType="ghost_icon"
              size="small"
              onClick={onClearSelection}
            >
              <Cross />
            </Button>
          </>
        )}
      </SelectedFloatingControl>
      <DeleteConfirmationModal
        {...deleteModal}
        isLoading={isDeleting}
        confirm={handleBulkDelete}
        type="MULTIPLE"
        itemCount={selectedItemCount}
      />
      <RestoreConfirmation
        {...restoreModal}
        isLoading={isRestoring}
        confirm={handleBulkRestore}
        itemCount={selectedItemCount}
      />
    </>
  );
};
